import { motion, useMotionValue, useSpring } from "framer-motion";
import { DeviceShowcase } from "@/components/DeviceShowcase";

const MaskedLine = ({ children, delay = 0 }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.15, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateY = useSpring(mx, { stiffness: 55, damping: 16 });
  const rotateX = useSpring(my, { stiffness: 55, damping: 16 });
  const gx = useMotionValue(-600);
  const gy = useMotionValue(-600);
  const glowX = useSpring(gx, { stiffness: 90, damping: 22, mass: 0.6 });
  const glowY = useSpring(gy, { stiffness: 90, damping: 22, mass: 0.6 });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set(((e.clientX - r.left) / r.width - 0.5) * 7);
    my.set(-((e.clientY - r.top) / r.height - 0.5) * 5);
    gx.set(e.clientX - r.left - 210);
    gy.set(e.clientY - r.top - 210);
  };
  const onLeave = () => {
    mx.set(0);
    my.set(0);
    gx.set(-600);
    gy.set(-600);
  };

  const learnMore = () => {
    if (window.__lenis) window.__lenis.scrollTo("#threat");
    else document.getElementById("threat")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      data-testid="hero-section"
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      className="relative flex min-h-screen flex-col items-center justify-center px-6 py-24"
    >
      <motion.div
        data-testid="hero-cursor-glow"
        className="cursor-glow pointer-events-none absolute left-0 top-0 h-[420px] w-[420px] bg-[#00D4FF] opacity-[0.07] blur-[60px]"
        style={{ x: glowX, y: glowY }}
        aria-hidden="true"
      />
      <div className="absolute inset-x-0 top-0 flex items-center justify-between px-6 py-6 sm:px-10">
        <div className="flex items-baseline">
          <span data-testid="brand-wordmark" className="font-serif text-lg tracking-wide text-[#C4A24C]">
            VoiceGuard<span className="font-bold">USB</span>™
          </span>
        </div>
        <span className="hidden text-xs uppercase tracking-[0.25em] text-[#6B7280] sm:block">
          Independently Held · Patent Pending 64/025,595
        </span>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.4, ease: "easeOut" }}
        style={{ rotateX, rotateY, transformPerspective: 1000 }}
      >
        <DeviceShowcase className="h-72 sm:h-96 lg:h-[26rem]" />
      </motion.div>

      <h1
        data-testid="hero-headline"
        className="mt-14 text-center font-serif text-4xl leading-tight text-[#C4A24C] sm:text-5xl lg:text-6xl"
      >
        <MaskedLine delay={0.4}>The Infrastructure</MaskedLine>
        <MaskedLine delay={0.58}>for Audio Trust.</MaskedLine>
      </h1>

      <motion.p
        data-testid="hero-subline"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.05 }}
        className="mt-6 text-center text-sm tracking-wide text-[#E4E5EA]/90 sm:text-base"
      >
        Hardware-rooted audio authentication. No frills. No external drivers. No cloud dependency.
      </motion.p>

      <motion.button
        data-testid="hero-learn-more-button"
        onClick={learnMore}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.3 }}
        className="mt-12 bg-[#C4A24C] px-10 py-4 text-sm font-semibold uppercase tracking-[0.25em] text-[#09090F] transition-colors duration-300 hover:bg-[#E8D08A]"
      >
        Learn More
      </motion.button>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.2, delay: 1.8 }}
        className="absolute bottom-8 h-12 w-px bg-[#C4A24C]/40"
        aria-hidden="true"
      />
    </section>
  );
}
